import type { ReactNode } from "react";
import React, {
    createContext,
    useState,
    useContext,
    useMemo,
    useCallback,
} from "react";

import { Progress } from "@radix-ui/react-progress";
import { useNavigation } from "@remix-run/react";
import { cx } from "class-variance-authority";
import ReactDOM from "react-dom";

import { logger } from "~/lib/logger";

interface LoaderContextType {
    isLoading: boolean;
    setLoading: (loading: boolean) => void;
}

const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

export const LoaderProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [isLoading, setIsLoading] = useState(false);

    // AIDEV-NOTE: Memoize setLoading to prevent unnecessary re-renders
    const setLoading = useCallback((loading: boolean) => {
        setIsLoading(loading);
    }, []);

    // AIDEV-NOTE: Memoize context value to prevent unnecessary re-renders
    const value = useMemo(
        () => ({ isLoading, setLoading }),
        [isLoading, setLoading],
    );

    return (
        <LoaderContext.Provider value={value}>
            {children}
            <PortalLoader />{" "}
            {/* Render the portal loader within the provider */}
        </LoaderContext.Provider>
    );
};

export const useLoader = (): LoaderContextType => {
    const context = useContext(LoaderContext);
    if (context === undefined) {
        throw new Error("useLoader must be used within a LoaderProvider");
    }
    return context;
};

// --- PortalLoader Component ---

const PortalLoader: React.FC = () => {
    const { isLoading } = useLoader();
    const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
    const [isHydrated, setIsHydrated] = useState(false);

    // Track hydration state
    React.useEffect(() => {
        setIsHydrated(true);
    }, []);

    // Find the portal root element once the component mounts
    React.useEffect(() => {
        if (import.meta.env.MODE === "test" || !isHydrated) {
            return;
        }
        const element = document.getElementById("loader-portal-root");
        if (element) {
            setPortalRoot(element);
        } else {
            console.warn(
                "🌀 PortalLoader: Portal root element #loader-portal-root not found in the DOM",
            );
        }
    }, [isHydrated, isLoading]);

    // Don't render portal until after hydration
    if (!isLoading || !portalRoot || !isHydrated) {
        return null;
    }

    // Use ReactDOM.createPortal to render the Loader into the portal root
    return ReactDOM.createPortal(
        // <div className="fixed inset-0 z-50 flex items-center justify-center">
        <Progress value={30} />,
        // </div>,
        portalRoot,
    );
};

function GlobalLoading() {
    const navigation = useNavigation();
    const active = navigation.state !== "idle";

    const ref = React.useRef<HTMLDivElement>(null);
    const [animationComplete, setAnimationComplete] = React.useState(true);

    React.useEffect(() => {
        if (!ref.current) return;
        if (active) setAnimationComplete(false);

        try {
            Promise.allSettled(
                ref.current.getAnimations().map(({ finished }) => finished),
            ).then(() => !active && setAnimationComplete(true));
        } catch (e) {}
    }, [active]);

    return (
        <div
            role="progressbar"
            aria-hidden={!active}
            aria-valuetext={active ? "Loading" : undefined}
            className="fixed inset-x-0 top-0 z-50 h-1 animate-pulse print:hidden"
        >
            <div
                ref={ref}
                className={cx(
                    "h-full bg-gradient-to-r from-brand-terra-200 to-brand-terra-300 transition-all duration-500 ease-in-out",
                    navigation.state === "idle" &&
                        animationComplete &&
                        "w-0 opacity-0 transition-none",
                    navigation.state === "submitting" && "w-4/12",
                    navigation.state === "loading" && "w-10/12",
                    navigation.state === "idle" &&
                        !animationComplete &&
                        "w-full",
                )}
            />
        </div>
    );
}

export { GlobalLoading };
